import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import find from 'lodash/find';
import map from 'lodash/map';
import reduce from 'lodash/reduce';
import get from 'lodash/get';
import { Button } from 'antd';
import { I18n } from 'react-redux-i18n';

import Pills from 'components/Pills';
import Pellet from 'components/Pellet';
import Select from 'components/Select';

import TitleComponent from '../TitleComponent';
import FeedItem from '../FeedItem';

const isMobile = window.innerWidth <= 845;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  grid-gap: 30px;
  position: absolute;
  height: auto;
  bottom: 0;
  top: 0;
  left: 0;
  right: 0;
  padding: 20px;
  background-color: ${(props) => props.theme.colors['white']};
  border-radius: 10px;
  min-height: 500px;

  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;

    @media (max-width: ${(props) =>
        props.theme.devices.isTablet ? '0px' : '845px'}) {
      display: contents;
      padding: 16px;
    }
  }

  .containerPillsDashboard {
    @media (max-width: ${(props) =>
        props.theme.devices.isTablet ? '0px' : '845px'}) {
      margin-left: 0;
      width: 100%;
    }
  }

  .containerPillsDashboard > * {
    margin: 0;
  }

  .title {
  }

  .btn-show-more {
    width: 100%;
    height: 3rem;
    border-radius: 5px;
  }

  @media (max-width: 845px) {
    padding: 16px;
  }

  @media (max-width: 1200px) {
    position: relative;
    height: 100vh;
    grid-gap: 16px;
  }

  @media (max-width: 845px) {
    height: auto;
  }
`;

const PillPos = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  & > p {
    margin-right: 4px;
    white-space: nowrap;
  }
`;

const FeedListContainer = styled.div`
  overflow: auto;
  display: block;
  flex: 1;
  flex-direction: column;

  @media (max-width: ${(props) =>
      props.theme.devices.isTablet ? '0px' : '845px'}) {
    margin-left: 0;
  }
`;

const LastReportContainer = styled.div`
  display: flex;
  flex: 0;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  background-color: ${(props) => props.theme.colors['dark']};
  padding: 20px 30px;
  border-radius: 10px;
  margin-top: -10px;
`;

const LastReportTextContent = styled.div`
  display: flex;

  & > p {
    font-family: ${(props) => props.theme.fonts.oswald};
    font-size: 14px;
    font-weight: normal;
    font-style: normal;
    font-stretch: normal;
    line-height: 23px;
    letter-spacing: normal;
    color: ${(props) => props.theme.colors['white']};
  }

  & > p.title {
    text-decoration: underline;
  }

  & > p.colum {
    padding: 0 4px;
  }
`;

const ButtonShowLastReport = styled(Button).attrs(() => ({
  type: 'primary',
  shape: 'round'
}))`
  background-color: ${(props) => props.theme.colors['terra-cotta']};
  color: ${(props) => props.theme.colors['white']};
  font-family: ${(props) => props.theme.fonts.oswald};
  border: none;
  padding: 3px;
  height: auto;

  &.ant-btn-primary:not(:disabled):not(.ant-btn-disabled):hover {
    background-color: ${(props) => props.theme.colors['terra-cotta']};
    color: ${(props) => props.theme.colors['white']};
    border: none;
  }
`;

const MarketInformationsComponent = ({ data }) => {
  const moreNumber = 20;
  const [nbDisplay, setNbDisplay] = useState(5);
  const [sortedData, setSortedData] = useState({});
  const [options, setOptions] = useState([]);

  useEffect(() => handleData(data), [data]);

  const handleOptionChange = ({ value }) => {
    setOptions(
      map(options, (opt) => ({
        ...opt,
        active: opt.value === value
      }))
    );
  };

  const handleShowMore = () => setNbDisplay(nbDisplay + moreNumber);

  const handleData = (data = []) => {
    const { distribution, press, notation } = reduce(
      data,
      (acc, curr) => {
        acc[curr.type].push(curr);
        return acc;
      },
      { distribution: [], press: [], notation: [] }
    );

    setSortedData({ distribution, press, notation });
    setOptions(
      getOptions({
        value: get(options, 'value', 'all'),
        nbDistrib: distribution.length,
        nbPress: press.length,
        nbNote: notation.length
      })
    );
  };

  const renderPillOption = (value, activeValue, label, nb, color) => ({
    value,
    label: renderLabelPillOptions(label, nb, color),
    active: value === activeValue
  });

  const getOptions = ({ value, nbDistrib, nbPress, nbNote }) => {
    return [
      {
        value: 'all',
        label: I18n.t('placeholderAll'),
        active: value === 'all'
      },
      nbDistrib &&
        nbDistrib > 0 &&
        renderPillOption(
          'distribution',
          value,
          I18n.t('dashboard.pillOptions.distribution'),
          nbDistrib,
          '#f06543'
        ),
      nbPress &&
        nbPress > 0 &&
        renderPillOption(
          'press',
          value,
          I18n.t('dashboard.pillOptions.press'),
          nbPress,
          '#bcb9a0'
        ),
      nbNote &&
        nbNote > 0 &&
        renderPillOption(
          'notation',
          value,
          I18n.t('dashboard.pillOptions.notation'),
          nbNote,
          '#323739'
        )
    ].filter((item) => !!item);
  };

  const renderLabelPillOptions = (label, nb, color) => (
    <PillPos>
      <p>
        {label} ({nb})
      </p>
      <Pellet color={color} />
    </PillPos>
  );

  const renderFeedList = () => {
    const opt = find(options, { active: true });
    let selectedData =
      !opt || opt.value === 'all' ? data : sortedData[opt.value];

    selectedData.sort((a, b) => {
      if (a && b && a.date === b.date) {
        return a.order - b.order;
      }
      return 0;
    });

    let nbFeeds = selectedData.length;
    let nbMoreToShow = nbFeeds - nbDisplay;
    if (isMobile && nbDisplay < nbFeeds) {
      selectedData = selectedData.slice(0, nbDisplay);
      let nbToShow = nbDisplay + moreNumber;
      nbMoreToShow = nbMoreToShow >= nbToShow ? moreNumber : nbMoreToShow;
    }

    return (
      <FeedListContainer>
        {selectedData.map((item, index) => (
          <FeedItem key={index} {...item} />
        ))}
        {isMobile && nbDisplay < nbFeeds && (
          <div>
            <Button className="btn-show-more" onClick={handleShowMore}>
              {I18n.t('dashboard.btnShowMoreFeed')} (+{nbMoreToShow})
            </Button>
          </div>
        )}
      </FeedListContainer>
    );
  };

  const renderTypeFilter = () => {
    if (window.innerWidth < 375) {
      return (
        <Select
          options={options}
          value={options}
          onChange={handleOptionChange}
          dropdownProps={{ styles: `margin-right: 15px` }}
        />
      );
    }

    return (
      <Pills
        options={options}
        onChange={handleOptionChange}
        styles={`
          justify-content: space-between;
          margin: 10px 20px;
          min-height: 26px;
        `}
      />
    );
  };

  // eslint-disable-next-line no-unused-vars,@typescript-eslint/no-unused-vars
  const renderLastReportContainer = () => {
    return (
      <LastReportContainer>
        <LastReportTextContent>
          <p className="title">
            {I18n.t('dashboard.monthlyReportTitle').toUpperCase()}
          </p>
          <p className="colum">:</p>
          <p>{I18n.t('dashboard.monthlyReportSubtitle')}</p>
        </LastReportTextContent>
        <ButtonShowLastReport>
          {I18n.t('dashboard.monthlyReportButton').toUpperCase()}
        </ButtonShowLastReport>
      </LastReportContainer>
    );
  };

  return (
    <Container>
      {/* Données non encore disponible en BACK */}
      {/* {renderLastReportContainer()} */}
      <div className="header">
        <TitleComponent>
          <p>{I18n.t('dashboard.marketInformationTitle').toUpperCase()}</p>
        </TitleComponent>
        <div className="containerPillsDashboard">{renderTypeFilter()}</div>
      </div>
      {renderFeedList()}
    </Container>
  );
};

export default MarketInformationsComponent;
